// The printable recovery kit: an A4 PDF, a plain-text copy and a short README, optionally zipped
// into one tidy folder. Pure functions (no DOM), so they are testable; platform/recoveryKit.web.ts
// does the opening, saving and sharing. The codes live only in memory: nothing here talks to a server.
import { PDFArray, PDFDocument, PDFFont, PDFPage, StandardFonts, decodePDFRawStream, rgb } from '@cantoo/pdf-lib';
import qrcode from 'qrcode-generator';
import { strToU8, zipSync } from 'fflate';

/** Every user-visible string, already translated (the PDF lives outside React). */
export type KitStrings = {
  brand: string;
  title: string;
  /** e.g. "Ada · created 31 Oct 2026" */
  account: string;
  intro: string;
  codesHeading: string;
  tickHint: string;
  stepsTitle: string;
  steps: string[];
  keepTitle: string;
  keep: string[];
  qrCaption: string;
  footer: string;
  readme: string;
  /** Printed on a password-protected sheet (never the password itself). */
  encryptedNote: string;
  /** The read-me of a protected kit: no plain-text copy, and a reminder to keep the password apart. */
  readmeEncrypted: string;
};

/** `password`: encrypt the PDF with it (AES-256) and leave out the plain-text copy. */
export type KitInput = { codes: string[]; displayName: string; signInUrl: string; strings: KitStrings; date: Date; password?: string };

const A4 = { w: 595.28, h: 841.89 };
const KEYWORD_MARK = 'chefi-recovery-v1';
const CODE = /[A-HJ-NP-Z2-9]{4}(?:-[A-HJ-NP-Z2-9]{4}){2}/g;
const col = (hex: string) => rgb(parseInt(hex.slice(1, 3), 16) / 255, parseInt(hex.slice(3, 5), 16) / 255, parseInt(hex.slice(5, 7), 16) / 255);
// The app's paper palette (theme.ts), so the sheet looks like Chefi.
const C = { paper: col('#F2ECE2'), card: col('#E8E0D3'), border: col('#DCD2C3'), ink: col('#2E2925'), soft: col('#7A7068'), tint: col('#B5573B') };

/** The standard PDF fonts only cover Latin-1: map the common typographic characters, drop the rest. */
export function latin1(text: string): string {
  return text
    .replace(/[‘’‚]/g, "'").replace(/[“”„]/g, '"').replace(/[–—]/g, '-').replace(/…/g, '...').replace(/→/g, '>').replace(/←/g, '<').replace(/ /g, ' ')
    .replace(/[^\x20-\x7E -ÿ\n]/g, '?');
}

function wrap(text: string, font: PDFFont, size: number, width: number): string[] {
  const lines: string[] = [];
  for (const para of latin1(text).split('\n')) {
    let line = '';
    for (const word of para.split(/\s+/).filter(Boolean)) {
      const next = line ? `${line} ${word}` : word;
      if (line && font.widthOfTextAtSize(next, size) > width) {
        lines.push(line);
        line = word;
      } else line = next;
    }
    lines.push(line);
  }
  return lines;
}

function roundedRect(page: PDFPage, x: number, yTop: number, w: number, h: number, r: number, opts: { color?: ReturnType<typeof rgb>; border?: ReturnType<typeof rgb> }) {
  const path = `M ${r} 0 H ${w - r} A ${r} ${r} 0 0 1 ${w} ${r} V ${h - r} A ${r} ${r} 0 0 1 ${w - r} ${h} H ${r} A ${r} ${r} 0 0 1 0 ${h - r} V ${r} A ${r} ${r} 0 0 1 ${r} 0 Z`;
  page.drawSvgPath(path, { x, y: yTop, color: opts.color, borderColor: opts.border, borderWidth: opts.border ? 0.8 : 0 });
}

function drawQr(page: PDFPage, value: string, x: number, y: number, size: number) {
  const qr = qrcode(0, 'M');
  qr.addData(value);
  qr.make();
  const n = qr.getModuleCount();
  const quiet = 2;
  const unit = size / (n + quiet * 2);
  page.drawRectangle({ x, y, width: size, height: size, color: rgb(1, 1, 1), borderColor: C.border, borderWidth: 0.8 });
  for (let r = 0; r < n; r++)
    for (let c = 0; c < n; c++)
      if (qr.isDark(r, c)) page.drawRectangle({ x: x + (c + quiet) * unit, y: y + size - (r + quiet + 1) * unit, width: unit + 0.15, height: unit + 0.15, color: C.ink });
}

export async function buildPdf(input: KitInput): Promise<Uint8Array> {
  const { strings: t, codes } = input;
  const pdf = await PDFDocument.create();
  pdf.setTitle(latin1(`${t.brand} - ${t.title}`));
  pdf.setAuthor(t.brand);
  pdf.setCreationDate(input.date);
  const page = pdf.addPage([A4.w, A4.h]);
  const serif = await pdf.embedFont(StandardFonts.TimesRoman);
  const serifBold = await pdf.embedFont(StandardFonts.TimesRomanBold);
  const serifItalic = await pdf.embedFont(StandardFonts.TimesRomanItalic);
  const mono = await pdf.embedFont(StandardFonts.CourierBold);
  const M = 56;
  const inner = A4.w - M * 2;

  page.drawRectangle({ x: 0, y: 0, width: A4.w, height: A4.h, color: C.paper });
  page.drawRectangle({ x: 22, y: 22, width: A4.w - 44, height: A4.h - 44, borderColor: C.border, borderWidth: 1 });

  // Header
  let y = A4.h - 96;
  page.drawText(latin1(t.brand), { x: M, y, size: 46, font: serifBold, color: C.tint });
  page.drawText(latin1(t.title), { x: M, y: y - 30, size: 22, font: serifItalic, color: C.ink });
  page.drawText(latin1(t.account), { x: M, y: y - 52, size: 11, font: serif, color: C.soft });
  page.drawRectangle({ x: M, y: y - 68, width: 46, height: 2.5, color: C.tint });
  page.drawLine({ start: { x: M + 52, y: y - 67 }, end: { x: A4.w - M, y: y - 67 }, thickness: 0.8, color: C.border });
  y -= 96;

  // Intro
  for (const line of wrap(t.intro, serif, 11.5, inner)) {
    page.drawText(line, { x: M, y, size: 11.5, font: serif, color: C.ink });
    y -= 16;
  }
  y -= 12;

  // Codes: two columns, five rows, each with a box to tick when used
  page.drawText(latin1(t.codesHeading), { x: M, y, size: 14, font: serifBold, color: C.ink });
  page.drawText(latin1(t.tickHint), { x: A4.w - M - serifItalic.widthOfTextAtSize(latin1(t.tickHint), 10), y: y + 1, size: 10, font: serifItalic, color: C.soft });
  y -= 14;
  const gap = 14;
  const cw = (inner - gap) / 2;
  const ch = 46;
  codes.forEach((code, i) => {
    const cx = M + (i % 2) * (cw + gap);
    const top = y - Math.floor(i / 2) * (ch + 10);
    roundedRect(page, cx, top, cw, ch, 9, { color: C.card, border: C.border });
    page.drawText(String(i + 1).padStart(2, '0'), { x: cx + 14, y: top - ch / 2 - 3.5, size: 9.5, font: serifBold, color: C.tint });
    let px = cx + 38;
    for (const ch2 of code) {
      page.drawText(ch2, { x: px, y: top - ch / 2 - 5, size: 15.5, font: mono, color: C.ink });
      px += mono.widthOfTextAtSize(ch2, 15.5) + 1.1;
    }
    page.drawRectangle({ x: cx + cw - 30, y: top - ch / 2 - 7, width: 14, height: 14, borderColor: C.soft, borderWidth: 1 });
  });
  y -= Math.ceil(codes.length / 2) * (ch + 10) + 26;

  // Steps
  page.drawText(latin1(t.stepsTitle), { x: M, y, size: 14, font: serifBold, color: C.ink });
  y -= 20;
  t.steps.forEach((step, i) => {
    page.drawCircle({ x: M + 8, y: y + 3.5, size: 8.5, color: C.tint });
    page.drawText(String(i + 1), { x: M + 5.2, y: y + 0.2, size: 10, font: serifBold, color: rgb(1, 0.98, 0.95) });
    for (const line of wrap(step, serif, 11, inner - 28)) {
      page.drawText(line, { x: M + 26, y, size: 11, font: serif, color: C.ink });
      y -= 15;
    }
    y -= 5;
  });
  y -= 16;

  // Bottom block: QR on the left, safekeeping notes on the right
  const qrSize = 104;
  const blockTop = y;
  drawQr(page, input.signInUrl, M, blockTop - qrSize, qrSize);
  const caption = wrap(t.qrCaption, serifItalic, 9.5, qrSize + 20);
  caption.forEach((line, i) => page.drawText(line, { x: M, y: blockTop - qrSize - 14 - i * 12, size: 9.5, font: serifItalic, color: C.soft }));
  const nx = M + qrSize + 34;
  let ny = blockTop - 4;
  page.drawText(latin1(t.keepTitle), { x: nx, y: ny, size: 14, font: serifBold, color: C.ink });
  ny -= 20;
  for (const note of t.keep) {
    page.drawCircle({ x: nx + 3, y: ny + 3.5, size: 2.2, color: C.tint });
    for (const line of wrap(note, serif, 11, A4.w - M - nx - 14)) {
      page.drawText(line, { x: nx + 14, y: ny, size: 11, font: serif, color: C.ink });
      ny -= 15;
    }
    ny -= 4;
  }

  // The codes also sit in the page as tiny text outside the printed area, so uploading the PDF later can
  // sign in without typing (see readCodesFromPdf). Being page content, it is encrypted with the rest.
  [KEYWORD_MARK, ...codes].forEach((text, i) => page.drawText(text, { x: 10, y: -30 - i * 4, size: 1, font: mono, color: C.paper }));

  // Footer
  if (input.password) {
    const note = latin1(t.encryptedNote);
    page.drawText(note, { x: (A4.w - serifItalic.widthOfTextAtSize(note, 9)) / 2, y: 54, size: 9, font: serifItalic, color: C.tint });
  }
  const foot = latin1(t.footer);
  page.drawText(foot, { x: (A4.w - serifItalic.widthOfTextAtSize(foot, 9)) / 2, y: 40, size: 9, font: serifItalic, color: C.soft });
  if (input.password) {
    // AES-256 (PDF 2.0, revision 6). The owner password is random and thrown away: nobody needs it.
    await pdf.encrypt({
      userPassword: input.password,
      ownerPassword: Array.from(crypto.getRandomValues(new Uint8Array(24)), b => b.toString(16).padStart(2, '0')).join(''),
      permissions: { printing: 'highResolution', copying: true, modifying: false, annotating: false },
    });
  }
  return pdf.save();
}

const dateStamp = (d: Date) => d.toISOString().slice(0, 10);

/** "Chefi Recovery - Ada - 2026-10-31": safe as a folder name on every system. */
export function folderName(displayName: string, date: Date): string {
  // eslint-disable-next-line no-control-regex
  const who = displayName.replace(/[\\/:*?"<>|\u0000-\u001f]/g, '').trim().slice(0, 30);
  return ['Chefi Recovery', who, dateStamp(date)].filter(Boolean).join(' - ');
}

export function buildText(input: KitInput): string {
  const t = input.strings;
  const lines = [`${t.brand} - ${t.title}`, t.account, '', t.intro, '', t.codesHeading, ''];
  input.codes.forEach((c, i) => lines.push(`[ ]  ${String(i + 1).padStart(2, ' ')}.  ${c}`));
  lines.push('', t.stepsTitle, ...t.steps.map((s, i) => `${i + 1}. ${s}`), '', t.keepTitle, ...t.keep.map(k => `- ${k}`), '', input.signInUrl);
  return `${lines.join('\n')}\n`;
}

export type KitFile = { name: string; data: Uint8Array; type: string };

export async function buildKit(input: KitInput): Promise<{ folder: string; files: KitFile[]; zip: () => Uint8Array; encrypted: boolean }> {
  const folder = folderName(input.displayName, input.date);
  const encrypted = !!input.password;
  const files: KitFile[] = [
    { name: 'READ ME FIRST.txt', data: strToU8(`${encrypted ? input.strings.readmeEncrypted : input.strings.readme}\n`), type: 'text/plain' },
    { name: encrypted ? 'Chefi recovery codes (password protected).pdf' : 'Chefi recovery codes.pdf', data: await buildPdf(input), type: 'application/pdf' },
  ];
  // A plain-text copy next to a protected PDF would make the password pointless.
  if (!encrypted) files.push({ name: 'Chefi recovery codes.txt', data: strToU8(buildText(input)), type: 'text/plain' });
  return { folder, files, encrypted, zip: () => zipSync(Object.fromEntries(files.map(f => [`${folder}/${f.name}`, f.data])), { level: 6 }) };
}

export class PdfNeedsPassword extends Error {}
export class NotARecoveryPdf extends Error {}

/** Text shown by the page's content stream: `<hex> Tj` and `(text) Tj` operators. */
function pageStrings(doc: PDFDocument): string {
  const contents = doc.getPage(0).node.Contents();
  const streams = contents instanceof PDFArray ? contents.asArray().map(r => doc.context.lookup(r)) : [contents];
  let out = '';
  for (const stream of streams) {
    if (!stream) continue;
    const text = new TextDecoder('latin1').decode(decodePDFRawStream(stream as Parameters<typeof decodePDFRawStream>[0]).decode());
    for (const m of text.matchAll(/<([0-9A-Fa-f\s]+)>\s*Tj|\(((?:[^()\\]|\\.)*)\)\s*Tj/g)) {
      out += m[1] ? (m[1].replace(/\s/g, '').match(/../g) ?? []).map(h => String.fromCharCode(parseInt(h, 16))).join('') : m[2];
      out += '\n';
    }
  }
  return out;
}

/**
 * Reads the recovery codes back out of an uploaded Chefi recovery PDF (nothing leaves the device).
 * Throws PdfNeedsPassword for a protected file without (or with a wrong) password, NotARecoveryPdf otherwise.
 */
export async function readCodesFromPdf(bytes: Uint8Array, password?: string): Promise<string[]> {
  let doc: PDFDocument;
  try {
    doc = await PDFDocument.load(bytes, password ? { password } : {});
  } catch (e) {
    if (/password|encrypt/i.test(String((e as Error)?.message))) throw new PdfNeedsPassword();
    throw new NotARecoveryPdf();
  }
  let strings = '';
  try {
    strings = pageStrings(doc);
  } catch {
    throw new NotARecoveryPdf();
  }
  const codes = strings.includes(KEYWORD_MARK) ? [...new Set(strings.match(CODE) ?? [])] : [];
  if (!codes.length) throw new NotARecoveryPdf();
  return codes;
}
